import { useEffect, useRef } from 'react';
import maplibregl, { type Map as MapLibreMap } from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';

type RouteCoordinate = [number, number];

type Props = {
  coordinates: RouteCoordinate[];
  activityName: string;
};

const STYLE_URL = import.meta.env.VITE_OPENFREE_STYLE_URL || 'https://tiles.openfreemap.org/styles/liberty';

function createRouteFeature(coordinates: RouteCoordinate[]) {
  return {
    type: 'Feature' as const,
    properties: {},
    geometry: {
      type: 'LineString' as const,
      coordinates,
    },
  };
}

function createEndpointElement(kind: 'start' | 'finish') {
  const element = document.createElement('div');
  element.className = `activity-route-marker activity-route-marker--${kind}`;
  element.innerHTML = kind === 'start' ? '<span>Mulai</span>' : '<span>Selesai</span>';
  return element;
}

export function ActivityRouteMap({ coordinates, activityName }: Props) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<MapLibreMap | null>(null);
  const markersRef = useRef<maplibregl.Marker[]>([]);
  const coordinatesRef = useRef(coordinates);

  coordinatesRef.current = coordinates;

  useEffect(() => {
    const container = containerRef.current;
    if (!container || mapRef.current) return;

    const initial: RouteCoordinate = coordinatesRef.current[0] ?? [106.8272, -6.186];
    const map = new maplibregl.Map({
      container,
      style: STYLE_URL,
      center: initial,
      zoom: 13,
      pitch: 0,
      bearing: 0,
      dragRotate: false,
      canvasContextAttributes: {
        antialias: true,
      },
      attributionControl: {
        compact: true,
      },
    });

    mapRef.current = map;
    map.addControl(new maplibregl.NavigationControl({ showCompass: false }), 'top-right');

    const resizeObserver = new ResizeObserver(() => map.resize());
    resizeObserver.observe(container);

    const drawRoute = (routeCoordinates: RouteCoordinate[]) => {
      if (routeCoordinates.length < 2) return;

      const routeFeature = createRouteFeature(routeCoordinates);
      const existingSource = map.getSource('activity-route') as maplibregl.GeoJSONSource | undefined;

      if (existingSource) {
        existingSource.setData(routeFeature);
      } else {
        map.addSource('activity-route', {
          type: 'geojson',
          data: routeFeature,
        });

        map.addLayer({
          id: 'activity-route-halo',
          type: 'line',
          source: 'activity-route',
          layout: {
            'line-cap': 'round',
            'line-join': 'round',
          },
          paint: {
            'line-color': '#ffffff',
            'line-width': 10,
            'line-opacity': 0.92,
          },
        });

        map.addLayer({
          id: 'activity-route-line',
          type: 'line',
          source: 'activity-route',
          layout: {
            'line-cap': 'round',
            'line-join': 'round',
          },
          paint: {
            'line-color': '#f58a1f',
            'line-width': 5,
            'line-opacity': 1,
          },
        });
      }

      markersRef.current.forEach((marker) => marker.remove());
      markersRef.current = [];

      const start = routeCoordinates[0];
      const finish = routeCoordinates[routeCoordinates.length - 1];
      if (start) {
        markersRef.current.push(
          new maplibregl.Marker({ element: createEndpointElement('start'), anchor: 'bottom' })
            .setLngLat(start)
            .addTo(map),
        );
      }
      if (finish) {
        markersRef.current.push(
          new maplibregl.Marker({ element: createEndpointElement('finish'), anchor: 'bottom' })
            .setLngLat(finish)
            .addTo(map),
        );
      }

      const bounds = new maplibregl.LngLatBounds();
      routeCoordinates.forEach((coordinate) => bounds.extend(coordinate));
      map.fitBounds(bounds, {
        padding: { top: 54, right: 46, bottom: 54, left: 46 },
        maxZoom: 16,
        duration: 700,
      });
    };

    map.on('load', () => drawRoute(coordinatesRef.current));

    return () => {
      resizeObserver.disconnect();
      markersRef.current.forEach((marker) => marker.remove());
      markersRef.current = [];
      map.remove();
      mapRef.current = null;
    };
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || coordinates.length < 2 || !map.isStyleLoaded()) return;

    const routeFeature = createRouteFeature(coordinates);
    const source = map.getSource('activity-route') as maplibregl.GeoJSONSource | undefined;
    source?.setData(routeFeature);

    markersRef.current.forEach((marker) => marker.remove());
    markersRef.current = [];

    const start = coordinates[0];
    const finish = coordinates[coordinates.length - 1];
    if (start) {
      markersRef.current.push(
        new maplibregl.Marker({ element: createEndpointElement('start'), anchor: 'bottom' })
          .setLngLat(start)
          .addTo(map),
      );
    }
    if (finish) {
      markersRef.current.push(
        new maplibregl.Marker({ element: createEndpointElement('finish'), anchor: 'bottom' })
          .setLngLat(finish)
          .addTo(map),
      );
    }

    const bounds = new maplibregl.LngLatBounds();
    coordinates.forEach((coordinate) => bounds.extend(coordinate));
    map.fitBounds(bounds, {
      padding: { top: 54, right: 46, bottom: 54, left: 46 },
      maxZoom: 16,
      duration: 650,
    });
  }, [coordinates]);

  return (
    <div className="activity-route-map" role="img" aria-label={`Peta rute 2D ${activityName}`}>
      <div ref={containerRef} className="activity-route-map__canvas" />
      <div className="activity-route-map__badge"><span /> Rute mengikuti titik aktivitas</div>
    </div>
  );
}
